/**
 * C1a's cache (`KLQ-20260819-005`).
 *
 * **Nothing polls and nothing subscribes**, with exactly one exception that is not in this
 * file: the upload's own state loop in `upload.ts`, which runs while a drawer is open and
 * stops when the file resolves. A drawing tree changes when somebody on this screen changes
 * it, so publishing invalidates the two queries a new version can alter — the current-version
 * index and that slot's history — and nothing else.
 *
 * **Shape whole, contents per branch** (`KLQ-20260825-008` §5.1, owner's decision 2026-09-10).
 * The hierarchy is one query per project session and is never partial — thirteen files read
 * this tree and several of them, `locationText` and the spreadsheet import among them, have no
 * honest answer to *"what if the node is not loaded"*. The **contents** are the other two: the
 * slots and their current versions, and those are keyed on the level they were asked for, so
 * expanding a floor fetches that floor.
 *
 * A level's two queries stay a pair. Each is still one request — a fourth per slot would be
 * the N+1 the whole D1 endpoint exists to avoid — so the per-slot queries here remain the
 * three a *selection* needs (history, next name, and the drawing's former addresses), keyed on
 * the slot and never fetched for the tree.
 */

import {
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { PROJECTS_QUERY_KEY } from '@/features/projects';

import type {
  Branch,
  ContainerKind,
  CurrentVersion,
  CurrentVersionPage,
  LevelPlan,
  NodeMove,
  PublishBody,
  PublishFromFilesItem,
  Version,
  Slot,
  SlotBulkEdit,
  SlotPage,
} from './api';
import {
  bulkEditSlots,
  correctVersionPhase,
  createNode,
  createSlot,
  duplicateNode,
  generateHierarchy,
  groupNodes,
  listCurrentVersions,
  readDrawingSummary,
  listDisciplines,
  listDocumentTypes,
  readDocumentApplicability,
  listPresets,
  listSchemes,
  listVersions,
  listSlots,
  proposeFromFiles,
  publishFromFiles,
  publishVersion,
  readHierarchy,
  readNextVersion,
  readSlot,
  removeNode,
  renameNode,
  reorderHierarchy,
  rollbackVersion,
  seedHierarchy,
} from './api';

function drawingsKey(projectId: string) {
  return [...PROJECTS_QUERY_KEY, 'drawings', projectId] as const;
}

export function hierarchyQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...drawingsKey(projectId), 'hierarchy'] as const,
    queryFn: () => readHierarchy(projectId),
    // `Infinity`, not a window: every writer that changes the tree already sweeps this entry —
    // `invalidateTree()` below invalidates the whole `drawingsKey(projectId)` prefix, and the
    // hierarchy key is under it. A sixty-second window buys nothing and costs one refetch per
    // mount, which is exactly what the criterion forbids.
    staleTime: Infinity,
  });
}

/**
 * One level's drawings — or, with no branch, the whole project.
 *
 * **The branch is in the key**, which is the whole mechanism: each expanded level is its own
 * cache entry, so opening a second floor fetches the second floor and re-renders the first
 * from memory. The whole-project entry stays its own too — the importer and the slicer rail
 * legitimately want every drawing, and them sharing a key with a level would mean one of them
 * silently reading the other's answer.
 *
 * The key stays under `drawingsKey(projectId)`, so `invalidateDrawings` still sweeps every
 * branch at once: a publish or a move changes a picture, not a subtree.
 */
export function slotsQueryOptions(projectId: string, branch: Branch = {}) {
  return queryOptions({
    queryKey: [...drawingsKey(projectId), 'slots', branch] as const,
    queryFn: () => listSlots(projectId, branch),
    staleTime: 60_000,
  });
}

/**
 * The overview's figures, under the drawings prefix so `invalidateDrawings` reaches them too;
 * a publish marks them stale like every other `currents` entry and the overview refetches on
 * its next mount (60 s, the same window the rows have).
 */
export function drawingSummaryQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...drawingsKey(projectId), 'summary'] as const,
    queryFn: () => readDrawingSummary(projectId),
    staleTime: 60_000,
  });
}

/** The version pills for the same branch, keyed the same way and fetched beside it. */
export function currentVersionsQueryOptions(projectId: string, branch: Branch = {}) {
  return queryOptions({
    queryKey: [...drawingsKey(projectId), 'currents', branch] as const,
    queryFn: () => listCurrentVersions(projectId, branch),
    staleTime: 60_000,
  });
}

/**
 * The one branch shape this screen asks for: **the drawings filed directly on this level.**
 *
 * `depth: 2` because the endpoint counts levels from the slice root and `1` is the root
 * itself. Written once, here, rather than at each call site: a screen that asked for `2` in
 * one place and left it out in another would fetch a whole subtree from the second and never
 * notice, because the answer is a superset and everything would still render.
 */
function levelOf(nodeId: string, narrowed: Branch = {}): Branch {
  return { ...narrowed, under: [nodeId], depth: 2 };
}

/**
 * The seven shapes a project can start from — reference data in the sense of
 * `MCK-20260812-006` G: identical for every workspace and changing on the order of releases.
 * Fetched once per session, and only when the seed panel is actually rendered.
 */
export function presetsQueryOptions() {
  return queryOptions({
    queryKey: ['hierarchy-presets'] as const,
    queryFn: () => listPresets(),
    staleTime: 24 * 60 * 60_000,
  });
}

/**
 * One drawing's own row, for the one field the tree does not carry: the addresses it used to
 * have (`MCK-20260812-011` §Naming).
 *
 * **Keyed on the slot, exactly like the version history beside it, and never asked for a tree
 * row.** The detail pane draws from `SlotNode` — the tree's join — and everything on screen
 * comes from there; this query is issued once when a drawing is selected and answers the
 * question the register deliberately does not: *what was this called before?* A row-level read
 * of it would be one request per drawing on a screen that exists to show hundreds.
 *
 * The same sixty seconds the tree's contents get. Aliases are written by a phase advance or a
 * template change — an act on this screen, which sweeps the whole `drawingsKey` prefix — so a
 * shorter window would only buy refetches of a list that barely changes.
 */
export function slotQueryOptions(projectId: string, slotId: string) {
  return queryOptions({
    queryKey: [...drawingsKey(projectId), 'slot', slotId] as const,
    queryFn: () => readSlot(projectId, slotId),
    staleTime: 60_000,
  });
}

export function versionsQueryOptions(projectId: string, slotId: string) {
  return queryOptions({
    queryKey: [...drawingsKey(projectId), 'versions', slotId] as const,
    queryFn: () => listVersions(projectId, slotId),
    staleTime: 30_000,
  });
}

/**
 * What the next publish will be called.
 *
 * `staleTime: 0` on purpose, unlike everything else here: it is read when a drawer opens, and
 * an answer cached from before somebody else published would print the wrong number in the
 * one sentence whose whole job is to be exact.
 */
export function nextVersionQueryOptions(projectId: string, slotId: string) {
  return queryOptions({
    queryKey: [...drawingsKey(projectId), 'next', slotId] as const,
    queryFn: () => readNextVersion(projectId, slotId),
    staleTime: 0,
  });
}

/**
 * What a batch of file names would become (`KLQ-20261023-010`). Keyed on the names in order,
 * under the drawings prefix so any write to the tree marks it stale, and read at open like
 * `nextVersionQueryOptions` — a proposal cached from before somebody else's publish would
 * promise a version that is already taken.
 */
export function fileProposalsQueryOptions(projectId: string, filenames: readonly string[]) {
  return queryOptions({
    queryKey: [...drawingsKey(projectId), 'file-proposals', filenames] as const,
    queryFn: () => proposeFromFiles(projectId, filenames),
    staleTime: 0,
  });
}

export function useHierarchy(projectId: string) {
  return useQuery(hierarchyQueryOptions(projectId));
}

export function useSlots(projectId: string, branch: Branch = {}) {
  return useQuery(slotsQueryOptions(projectId, branch));
}

export function useCurrentVersions(projectId: string, branch: Branch = {}) {
  return useQuery(currentVersionsQueryOptions(projectId, branch));
}

/** No branch is short of rows. A shared constant so the identity is stable across renders. */
const EMPTY_BRANCHES: ReadonlySet<string> = new Set();

/** What a screen has of the tree's contents right now, merged across the open branches. */
export interface BranchContents {
  readonly slots: readonly Slot[];
  readonly currents: readonly CurrentVersion[];
  /** True while any open level is on its first fetch — never on a background refetch. */
  readonly loading: boolean;
  readonly error: Error | null;
  /** Levels with more drawings than have been fetched — the ones that draw a sentinel row. */
  readonly more: ReadonlySet<string>;
  /** Ask one level for another page. Stable, so it may sit in an effect's dependencies. */
  readonly reach: (nodeId: string) => void;
}

/**
 * The contents of every open level, as one pair of lists.
 *
 * **One query per level, merged here** — not one query over a growing `under=` list. The
 * difference is the whole point: a union in the key changes on every expand, so every level
 * already fetched would be fetched again, and the tenth expand would cost ten levels. Keyed
 * separately, the tenth expand costs the tenth level.
 *
 * **One level, never a subtree**, which is what makes this need no pruning at all. The bound
 * is `depth: 2` — the drawings filed directly on that level — so two open levels cannot
 * overlap however they nest, and a parent being open does not pre-empt its children. The
 * alternative, subtree fetches with an *"skip what an open ancestor already covers"* rule, was
 * the first design and it dies on the root: the project root is open before anybody presses
 * anything, and a subtree fetch of the root is the whole project.
 *
 * `levels` is what the table reported as open, so a level is fetched exactly while its row is
 * showing its children. Closing one stops the query; react-query keeps the answer, so
 * reopening it is a render.
 *
 * Still deduplicated on id when merged: an invalidation racing an expand can briefly leave two
 * answers in flight, and a drawing drawn twice is a worse failure than one drawn late.
 */
export function useBranchContents(
  projectId: string,
  levels: readonly string[],
  narrowed: Branch = {},
): BranchContents {
  /**
   * How many pages of each open level have been asked for. Absent is one.
   *
   * **A branch used to take whatever one request gave it, which was fifty** — so a floor with
   * eighty drawings drew fifty under a header that said eighty, and said nothing about the
   * rest. The count was right; the rows were the ones lying. The tasks plan got a sentinel row
   * per branch on 2026-09-10 and this screen did not, which is how the two hierarchies ended up
   * behaving differently.
   */
  const [pages, setPages] = useState<Readonly<Record<string, number>>>({});
  const branchOf = (id: string): Branch => ({
    ...levelOf(id, narrowed),
    pages: pages[id] ?? 1,
  });

  const slotPages = useQueries({
    queries: levels.map((id) => slotsQueryOptions(projectId, branchOf(id))),
  });
  const currentPages = useQueries({
    queries: levels.map((id) =>
      currentVersionsQueryOptions(projectId, branchOf(id)),
    ),
  });

  const reach = useCallback((nodeId: string) => {
    setPages((current) => ({ ...current, [nodeId]: (current[nodeId] ?? 1) + 1 }));
  }, []);

  const slots = new Map<string, Slot>();
  const currents = new Map<string, CurrentVersion>();
  const more = new Set<string>();
  let loading = false;
  let error: Error | null = null;

  slotPages.forEach((page, index) => {
    if (page.isPending) loading = true;
    if (page.error !== null && error === null) error = page.error;
    const id = levels[index];
    if (id !== undefined && page.data?.has_more === true) more.add(id);
    for (const item of page.data?.items ?? []) slots.set(item.id, item);
  });
  for (const page of currentPages) {
    if (page.isPending) loading = true;
    if (page.error !== null && error === null) error = page.error;
    for (const item of page.data?.items ?? []) currents.set(item.id, item);
  }

  return {
    slots: [...slots.values()],
    currents: [...currents.values()],
    loading,
    error,
    more,
    reach,
  };
}

/**
 * The drawings screen's contents — **per level, unless the arrangement has left the tree.**
 *
 * Fetching a level when its row is expanded works because the row is what asks. Three of this
 * screen's controls take that away, and each of them takes it away completely:
 *
 * - **A phase filter prunes the shape by a property of the contents.** A floor whose drawings
 *   have not been fetched holds nothing in any phase, so it would be dropped from the tree
 *   entirely — and the reader cannot expand a row that is not drawn to find out otherwise.
 * - **The object pivot re-parents by the same kind of property.** The headings are invented,
 *   the levels are gone, and there is no row left whose expansion means *"fetch this floor"*.
 * - **Flat mode drops the group rows.** No twisty exists, so no branch can ever be opened.
 *
 * `narrowed` rides along either way — it is the rail's non-location axes, and they are a
 * *server* filter (`GET /slots` takes them), so they belong in the key of whatever is fetched
 * rather than applied afterwards. Changing one refetches the open levels and nothing else.
 *
 * In all three the screen fetches the project, once, cached for the session. That is the
 * honest trade: the alternative is not a cheaper screen but a wrong one, showing a subset and
 * calling it the register. `KLQ-20260827-002`'s *"a phase switch issues no request"* is
 * narrowed by exactly this much and no more — the first switch away from *All phases* fetches;
 * every switch after it is a render, because the answer it filters is already whole.
 */
export function useDrawingsContents(
  projectId: string,
  levels: readonly string[],
  wholeProject: boolean,
  narrowed: Branch = {},
): BranchContents {
  const perLevel = useBranchContents(
    projectId,
    wholeProject ? [] : levels,
    narrowed,
  );
  const everySlot = useQuery({
    ...slotsQueryOptions(projectId, narrowed),
    enabled: wholeProject,
  });
  const everyCurrent = useQuery({
    ...currentVersionsQueryOptions(projectId, narrowed),
    enabled: wholeProject,
  });
  if (!wholeProject) return perLevel;
  return {
    slots: everySlot.data?.items ?? [],
    currents: everyCurrent.data?.items ?? [],
    loading: everySlot.isLoading || everyCurrent.isLoading,
    error: everySlot.error ?? everyCurrent.error,
    // The whole-project fetch has no branches to page: it is one answer, and the three controls
    // that force it (the phase filter, the object pivot, flat mode) are asking about the project
    // rather than about a level. `reach` is `perLevel`'s so the identity stays stable across the
    // switch — nothing calls it in this arm.
    more: EMPTY_BRANCHES,
    reach: perLevel.reach,
  };
}

export function usePresets(enabled: boolean) {
  return useQuery({ ...presetsQueryOptions(), enabled });
}

/** One drawing's own row — `enabled` only once a drawing is actually selected. */
export function useSlot(projectId: string, slotId: string | null) {
  return useQuery({
    ...slotQueryOptions(projectId, slotId ?? 'none'),
    enabled: slotId !== null,
  });
}

export function useVersions(projectId: string, slotId: string | null) {
  return useQuery({
    ...versionsQueryOptions(projectId, slotId ?? 'none'),
    enabled: slotId !== null,
  });
}

export function useNextVersion(projectId: string, slotId: string | null) {
  return useQuery({
    ...nextVersionQueryOptions(projectId, slotId ?? 'none'),
    enabled: slotId !== null,
  });
}

/** Everything the tree is drawn from — invalidated together, because it is one picture. */
function invalidateTree(projectId: string, queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: drawingsKey(projectId) });
}

/**
 * The same sweep, for a writer outside this feature.
 *
 * `owning_task_id` lives on the slot row, so a task generation that claims deliverables
 * (`KLQ-20260825-001` §5) makes the planliste and the tree wrong and nothing on the tasks
 * screen would otherwise say so. Exported rather than reproduced there: the key is this
 * feature's, and a second spelling of it is a cache that goes stale in one place only.
 */
export function invalidateDrawings(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
): void {
  invalidateTree(projectId, queryClient);
}

/**
 * How generated levels are named — fetched once and kept for the session.
 *
 * Reference data in the sense of `MCK-20260812-006` G, exactly like the presets: identical for
 * every workspace, changing on the order of releases. Only fetched when a generator surface is
 * actually rendered.
 */
export function schemesQueryOptions() {
  return queryOptions({
    queryKey: ['numbering-schemes'] as const,
    queryFn: () => listSchemes(),
    staleTime: 24 * 60 * 60_000,
  });
}

/**
 * The project's document-type vocabulary — reference data, one fetch per project session.
 *
 * A longer `staleTime` than the tree's: this list changes when somebody edits the project's
 * vocabulary, which is rare, and the generator is the only surface reading it today.
 */
export function documentTypesQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...drawingsKey(projectId), 'document-types'] as const,
    queryFn: () => listDocumentTypes(projectId),
    staleTime: 10 * 60_000,
  });
}

/**
 * The phase → document-type mapping. Reference data, cached hard, fetched once.
 *
 * Ten minutes like the types beside it: a mapping changes when a law changes or when the
 * studio overrides a standing, and neither is an event worth a subscription. **A phase switch
 * must not produce a request** — the whole mapping is in one response precisely so it does not.
 */
export function documentApplicabilityQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...drawingsKey(projectId), 'document-applicability'] as const,
    queryFn: () => readDocumentApplicability(projectId),
    staleTime: 10 * 60_000,
  });
}

export function useDocumentApplicability(projectId: string, enabled: boolean) {
  return useQuery({ ...documentApplicabilityQueryOptions(projectId), enabled });
}

export function disciplinesQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...drawingsKey(projectId), 'disciplines'] as const,
    queryFn: () => listDisciplines(projectId),
    staleTime: 10 * 60_000,
  });
}

export function useDisciplines(projectId: string, enabled: boolean) {
  return useQuery({ ...disciplinesQueryOptions(projectId), enabled });
}

export function useDocumentTypes(projectId: string, enabled: boolean) {
  return useQuery({ ...documentTypesQueryOptions(projectId), enabled });
}

export function useSchemes(enabled: boolean) {
  return useQuery({ ...schemesQueryOptions(), enabled });
}

/**
 * Generate levels from counts.
 *
 * **Not invalidated on a dry run.** A preview writes nothing, so throwing away the tree's
 * cache after one would be a refetch of data that did not change — and the preview is fired
 * on every keystroke in the counts.
 */
export function useGenerateHierarchy(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      readonly parent_id: string | null;
      readonly levels: readonly LevelPlan[];
      readonly dry_run: boolean;
    }) => generateHierarchy(projectId, body),
    retry: false,
    onSuccess: (_plan, body) => {
      if (!body.dry_run) invalidateTree(projectId, queryClient);
    },
  });
}

export function useDuplicateNode(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      readonly source_node_id: string;
      readonly count: number;
      readonly scheme: string;
      readonly dry_run: boolean;
    }) => duplicateNode(projectId, body),
    retry: false,
    onSuccess: (_plan, body) => {
      if (!body.dry_run) invalidateTree(projectId, queryClient);
    },
  });
}

export function useGroupNodes(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      readonly node_ids: readonly string[];
      readonly kind: ContainerKind;
      readonly name: string;
      readonly dry_run: boolean;
    }) => groupNodes(projectId, body),
    retry: false,
    onSuccess: (_result, body) => {
      if (!body.dry_run) invalidateTree(projectId, queryClient);
    },
  });
}

/**
 * Move levels (`KLQ-20260825-007` phase 2).
 *
 * No `dry_run` variant to branch on, unlike the three writers above: the endpoint has none.
 * So this always writes, and the number of re-addressed drawings arrives in the answer rather
 * than before it — which is why `ProjectShapeEditor` states an upper bound first and the
 * server's own figure afterwards.
 */
export function useReorderHierarchy(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (moves: readonly NodeMove[]) => reorderHierarchy(projectId, moves),
    retry: false,
    onSuccess: () => {
      invalidateTree(projectId, queryClient);
    },
  });
}

export function useSeedHierarchy(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preset: string) => seedHierarchy(projectId, preset),
    retry: false,
    onSuccess: () => {
      invalidateTree(projectId, queryClient);
    },
  });
}

export function useCreateNode(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      readonly parent_id: string | null;
      readonly kind: ContainerKind;
      readonly name: string;
    }) => createNode(projectId, body),
    retry: false,
    onSuccess: () => {
      invalidateTree(projectId, queryClient);
    },
  });
}

export function useRenameNode(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ nodeId, name }: { readonly nodeId: string; readonly name: string }) =>
      renameNode(projectId, nodeId, name),
    retry: false,
    onSuccess: () => {
      invalidateTree(projectId, queryClient);
    },
  });
}

export function useRemoveNode(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (nodeId: string) => removeNode(projectId, nodeId),
    retry: false,
    onSuccess: () => {
      invalidateTree(projectId, queryClient);
    },
  });
}

export function useCreateSlot(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      readonly parent_id: string | null;
      readonly name: string;
      readonly drawing_number?: string;
      readonly title?: string;
    }) => createSlot(projectId, body),
    retry: false,
    onSuccess: () => {
      invalidateTree(projectId, queryClient);
    },
  });
}

/**
 * One value over a selection of drawings — the grid's object column (`KLQ-20260828-001` §E).
 *
 * The tree is invalidated on success and not patched in place: filing under an object
 * re-renders identifiers whenever the template spends `{object}`, so the rows that changed are
 * not only the rows that were named. The grid's own optimistic overlay is what makes the
 * fan-out visible while that round trip happens.
 */
export function useBulkEditSlots(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SlotBulkEdit) => bulkEditSlots(projectId, body),
    retry: false,
    onSuccess: () => {
      invalidateTree(projectId, queryClient);
    },
  });
}

/**
 * Write a freshly current version into the pill caches instead of refetching them.
 *
 * `currentVersionsQueryOptions(projectId).queryKey` ends in `{}`, and TanStack matches keys
 * partially and *deep*: an empty object matches every branch, so invalidating it refetched one
 * `GET /current-versions?under=…` per open level — a publish on a project with six floors
 * open was six requests to redraw one pill (review 2026-09-25 §4.15). The response already
 * carries the pill: a `VersionResponse` has every field of a `CurrentVersionResponse`, so
 * the row is written wherever the slot's old pill is cached, and every branch is marked stale
 * with `refetchType: 'none'` for its next mount.
 *
 * **A slot with no pill yet is the case the write cannot reach** — a first publish, or a
 * branch whose slots are cached but whose pills are not. The `slots` caches say which branch
 * holds the slot (same `Branch` key, fetched beside it), so exactly that branch's pills are
 * refetched; a slot in no cached branch falls back to the whole-project entry, exactly, and
 * only if something is observing it.
 */
function writeCurrentVersion(
  queryClient: QueryClient,
  projectId: string,
  version: Version,
): void {
  const pill: CurrentVersion = {
    id: version.id,
    slot_id: version.slot_id,
    counter: version.counter,
    identifier: version.identifier,
    published_at: version.published_at,
    published_by_user_id: version.published_by_user_id ?? null,
    version_note: version.version_note,
  };
  const currents = [...drawingsKey(projectId), 'currents'] as const;
  let written = false;
  for (const [key, page] of queryClient.getQueriesData<CurrentVersionPage>({
    queryKey: currents,
  })) {
    if (!page?.items.some((row) => row.slot_id === pill.slot_id)) continue;
    written = true;
    queryClient.setQueryData<CurrentVersionPage>(key, {
      ...page,
      items: page.items.map((row) => (row.slot_id === pill.slot_id ? pill : row)),
    });
  }
  void queryClient.invalidateQueries({ queryKey: currents, refetchType: 'none' });
  if (written) return;

  const holding = queryClient
    .getQueriesData<SlotPage>({ queryKey: [...drawingsKey(projectId), 'slots'] })
    .filter(([, page]) => page?.items.some((slot) => slot.id === pill.slot_id) === true)
    .map(([key]) => key[key.length - 1]);
  const branches: Branch[] = holding.filter(
    (branch): branch is Branch => typeof branch === 'object' && branch !== null,
  );
  for (const branch of branches.length === 0 ? [{}] : branches) {
    void queryClient.invalidateQueries({
      queryKey: currentVersionsQueryOptions(projectId, branch).queryKey,
      exact: true,
    });
  }
}

export interface PublishVariables {
  readonly body: PublishBody;
  /** Minted per submission by the drawer and cleared by any edit — see `PublishDrawer`. */
  readonly idempotencyKey: string;
}

export function usePublishVersion(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }: PublishVariables) =>
      publishVersion(projectId, body, idempotencyKey),
    // A publish that failed on a `409` fails the same way on a retry, and one that failed
    // mid-flight may already have landed — the idempotency key makes a *deliberate* retry
    // safe, an automatic one merely invisible.
    retry: false,
    onSuccess: (version) => {
      writeCurrentVersion(queryClient, projectId, version);
      // The row's status is derived server-side from its current version (`_derived_status`),
      // and it lives on the slot rows the pill write does not touch — without this a drawing
      // published a moment ago still read „Plánované“ until a reload (`KLQ-20261023-010` J5-3).
      invalidateTree(projectId, queryClient);
    },
  });
}

export interface PublishFromFilesVariables {
  readonly items: readonly PublishFromFilesItem[];
  /** Minted per submission by the sheet and cleared by any edit — see `PublishDrawer`. */
  readonly idempotencyKey: string;
}

/**
 * The batch publish. A drawing the request created is a new row in some level, which no pill
 * write can draw — so the whole tree is invalidated, and each version is still written into
 * the pill caches so the rows already on screen change before the refetch lands.
 */
export function usePublishFromFiles(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ items, idempotencyKey }: PublishFromFilesVariables) =>
      publishFromFiles(projectId, items, idempotencyKey),
    // As `usePublishVersion`: the idempotency key makes a deliberate retry safe, an automatic
    // one merely invisible.
    retry: false,
    onSuccess: (published) => {
      invalidateTree(projectId, queryClient);
      for (const item of published) writeCurrentVersion(queryClient, projectId, item.version);
    },
  });
}

export function useRollbackVersion(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      versionId,
      idempotencyKey,
    }: {
      readonly versionId: string;
      readonly idempotencyKey: string;
    }) => rollbackVersion(versionId, idempotencyKey),
    retry: false,
    onSuccess: (version) => {
      writeCurrentVersion(queryClient, projectId, version);
      void queryClient.invalidateQueries({
        queryKey: versionsQueryOptions(projectId, version.slot_id).queryKey,
      });
    },
  });
}

/**
 * Correct a version's phase snapshot. The audit event is the server's; the client
 * invalidates the same two keys the rollback does, because a corrected phase changes
 * what the current-version pill and the slot's history both show.
 */
export function useCorrectVersionPhase(projectId: string, slotId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      versionId,
      phaseId,
    }: {
      readonly versionId: string;
      readonly phaseId: string | null;
    }) => correctVersionPhase(versionId, phaseId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: currentVersionsQueryOptions(projectId).queryKey,
      });
      if (slotId !== null) {
        void queryClient.invalidateQueries({
          queryKey: versionsQueryOptions(projectId, slotId).queryKey,
        });
      }
    },
  });
}
